import { useMutation, useQueryClient, QueryKey } from '@tanstack/react-query';
import { useErrorHandler } from '../../../lib/errorHandler';

interface EntityDialogOptions<TCreate, TUpdate, TEntity> {
	/**
	 * Display name for success/error messages (e.g., 'khoa', 'ngành')
	 */
	entityName: string;

	/**
	 * Query keys to invalidate on success
	 */
	queryKeys: QueryKey[];

	/**
	 * API function to create entity
	 */
	createFn: (data: TCreate) => Promise<TEntity>;

	/**
	 * API function to update entity
	 */
	updateFn: (id: string, data: TUpdate) => Promise<TEntity>;

	/**
	 * Callback when create/update succeeds
	 */
	onSuccess?: () => void;

	/**
	 * Custom success messages
	 */
	successMessages?: {
		create?: string;
		update?: string;
	};
}

interface EntityDialogReturn<TCreate, TUpdate> {
	createMutation: ReturnType<typeof useMutation<unknown, Error, TCreate>>;
	updateMutation: ReturnType<typeof useMutation<unknown, Error, { id: string; data: TUpdate }>>;
	isLoading: boolean;
	handleCreate: (data: TCreate) => void;
	handleUpdate: (id: string, data: TUpdate) => void;
}

/**
 * Hook for handling entity CRUD mutations in dialogs.
 * Extracts common mutation logic from dialog components.
 *
 * @param options - Configuration for the entity dialog
 * @returns Mutation handlers and loading state
 *
 * @example
 * const { createMutation, updateMutation, isLoading, handleCreate, handleUpdate } = useEntityDialog({
 *   entityName: 'khoa',
 *   queryKeys: [['faculties']],
 *   createFn: facultyApi.createFaculty,
 *   updateFn: facultyApi.updateFaculty,
 *   onSuccess: onClose,
 * });
 */
export function useEntityDialog<TCreate, TUpdate, TEntity = unknown>({
	entityName,
	queryKeys,
	createFn,
	updateFn,
	onSuccess,
	successMessages,
}: EntityDialogOptions<TCreate, TUpdate, TEntity>): EntityDialogReturn<TCreate, TUpdate> {
	const { showError, showSuccess } = useErrorHandler();
	const queryClient = useQueryClient();

	const defaultCreateMessage = `Thêm ${entityName} thành công`;
	const defaultUpdateMessage = `Cập nhật ${entityName} thành công`;

	const createMutation = useMutation({
		mutationFn: createFn,
		onSuccess: () => {
			showSuccess(successMessages?.create || defaultCreateMessage);
			queryKeys.forEach((key) => {
				queryClient.invalidateQueries({ queryKey: key });
			});
			onSuccess?.();
		},
		onError: (error: Error) => {
			showError(error.message || `Có lỗi xảy ra khi thêm ${entityName}`);
		},
	});

	const updateMutation = useMutation({
		mutationFn: ({ id, data }: { id: string; data: TUpdate }) => updateFn(id, data),
		onSuccess: () => {
			showSuccess(successMessages?.update || defaultUpdateMessage);
			queryKeys.forEach((key) => {
				queryClient.invalidateQueries({ queryKey: key });
			});
			onSuccess?.();
		},
		onError: (error: Error) => {
			showError(error.message || `Có lỗi xảy ra khi cập nhật ${entityName}`);
		},
	});

	const isLoading = createMutation.isPending || updateMutation.isPending;

	const handleCreate = (data: TCreate) => {
		createMutation.mutate(data);
	};

	const handleUpdate = (id: string, data: TUpdate) => {
		updateMutation.mutate({ id, data });
	};

	return {
		createMutation,
		updateMutation,
		isLoading,
		handleCreate,
		handleUpdate,
	};
}

export default useEntityDialog;
